import React, { useRef, useCallback, useMemo, useState } from 'react';
import { toast } from '../components/v2/V2Components';
import FileSourceSheet from '../components/v2/FileSourceSheet';
import { shouldUseFileSourceChooser } from '../utils/platform';

// Canonical `accept` strings. Bare `image/*` (not a specific MIME list) is what
// makes iOS Safari show its native chooser sheet on tap:
//
//   Take Photo (camera)  /  Photo Library  /  Choose File
//
// With an explicit list like `image/jpeg,image/png,image/heic,...` iOS often
// degrades to a file-browser-only picker and hides the camera option entirely.
// `image/*` covers HEIC implicitly on iOS (the OS classifies HEIC as an image),
// and the backend converts HEIC to JPEG at the upload boundary
// (utils/image_helpers.convert_heic_to_jpeg), so the wire format doesn't matter.
//
// Adding a new kind: prefer broad MIME globs (`image/*`, `application/pdf`)
// over enumerated subtypes. Adding `.heic` / specific extensions ALONGSIDE a
// glob is what breaks the iOS chooser — don't mix the two.
const ACCEPT_BY_KIND = {
    image: 'image/*',
    'image-or-pdf': 'image/*,application/pdf',
};

// Which tiles Hestia's own file-source sheet offers, per kind.
//
// Image-only kinds deliberately omit 'files': when the accept string is already
// `image/*`, a file-browse tile lands the user in the same picker as Photos,
// so it's noise.
const SOURCES_BY_KIND = {
    image: ['camera', 'photos'],
    'image-or-pdf': ['camera', 'photos', 'files'],
};

// Per-source input attributes, used only when the sheet is active.
//
// THE PHOTOS ENTRY IS THE WHOLE POINT — do not "unify" it with the kind's
// accept string. Android Chrome builds its intent chooser from the apps that
// can handle the declared MIME types, so `image/*,application/pdf` silently
// drops Google Photos and leaves the user with Camera + Files only. Narrowing
// the Photos input to `image/*` is what puts the gallery back, even for slots
// that also accept PDFs.
//
// Likewise, `capture` belongs on the camera input and NOWHERE else: on a
// non-camera input it forces the camera and removes the picker entirely.
const SOURCE_INPUT_PROPS = {
    camera: () => ({ accept: ACCEPT_BY_KIND.image, capture: 'environment' }),
    photos: () => ({ accept: ACCEPT_BY_KIND.image }),
    files: (kind) => ({ accept: ACCEPT_BY_KIND[kind] || ACCEPT_BY_KIND.image }),
};

/**
 * Hook for opening an OS file picker from a custom button.
 *
 * Consolidates the pattern of "hidden <input type=file> + ref + onClick"
 * that was repeated across ~14 surfaces, each making subtly different
 * `accept` choices that broke the iOS native chooser on some of them.
 *
 * On Android (see utils/platform.shouldUseFileSourceChooser) `triggerPicker`
 * opens Hestia's own Camera / Photos / Files sheet, which routes to one
 * narrowly-targeted hidden input per source. Everywhere else — iOS, desktop,
 * jsdom — it clicks a single hidden input exactly as it always has.
 *
 * Usage:
 *
 *   const { triggerPicker, fileInputElement } = useFilePicker({
 *       kind: 'image',
 *       onFiles: (files) => handleUpload(files[0]),
 *   });
 *
 *   return (
 *       <>
 *           <button onClick={triggerPicker}>Add Photo</button>
 *           {fileInputElement}
 *       </>
 *   );
 *
 * @param {Object} options
 * @param {'image' | 'image-or-pdf'} options.kind - File-type filter. Translates
 *   to a canonical, iOS-chooser-friendly `accept` string.
 * @param {boolean} [options.multiple=false] - Allow multi-select.
 * @param {(files: File[]) => void} options.onFiles - Called with the user's
 *   selected files as an Array (the hook always converts the raw FileList via
 *   Array.from before calling). Filtered by maxSizeMB if set. The hook resets
 *   the input value after each call so the SAME file can be re-selected next
 *   time (re-selecting the same file is a no-op on a raw input without this
 *   reset, surprising callers).
 * @param {boolean} [options.disabled=false] - When true, triggerPicker is a no-op.
 * @param {number} [options.maxSizeMB] - Optional client-side size limit per
 *   file (MB). Oversized files are dropped from the selection and a toast is
 *   shown. The server is still the source of truth — this is UX polish to
 *   avoid uploading a 50MB file just to have it rejected at the wire.
 * @param {string} [options.sheetTitle] - Heading for the file-source sheet.
 * @param {string} [options.testId='file-picker-input'] - Base `data-testid` for
 *   the hidden input(s). Override it when one component mounts the hook more
 *   than once (e.g. a photo picker and a document picker side by side) —
 *   otherwise both inputs carry the same testid and Playwright's strict mode
 *   fails on a selector that matches two elements.
 * @returns {{
 *   triggerPicker: () => void,
 *   fileInputElement: React.ReactElement,
 * }}
 */
export const useFilePicker = ({
    kind,
    multiple = false,
    onFiles,
    disabled = false,
    maxSizeMB,
    sheetTitle,
    testId = 'file-picker-input',
}) => {
    const inputRef = useRef(null);
    const sourceRefs = useRef({});
    const [sheetOpen, setSheetOpen] = useState(false);

    // Resolved once per hook instance: the platform doesn't change mid-session,
    // and re-reading matchMedia on every render would be wasted work.
    const useChooser = useMemo(() => shouldUseFileSourceChooser(), []);

    const sources = SOURCES_BY_KIND[kind] || SOURCES_BY_KIND.image;

    const triggerPicker = useCallback(() => {
        if (disabled) return;
        if (useChooser) {
            setSheetOpen(true);
            return;
        }
        inputRef.current?.click();
    }, [disabled, useChooser]);

    const handleChange = useCallback((e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            let accepted = Array.from(files);

            if (typeof maxSizeMB === 'number' && maxSizeMB > 0) {
                const limitBytes = maxSizeMB * 1024 * 1024;
                const tooBig = accepted.filter(f => f.size > limitBytes);
                accepted = accepted.filter(f => f.size <= limitBytes);

                if (tooBig.length === 1) {
                    toast.error(`"${tooBig[0].name}" exceeds the ${maxSizeMB}MB limit`);
                } else if (tooBig.length > 1) {
                    toast.error(`${tooBig.length} files exceed the ${maxSizeMB}MB limit and were skipped`);
                }
            }

            if (accepted.length > 0) {
                onFiles(accepted);
            }
        }
        // Reset so the same file can be re-selected next time without the
        // input thinking nothing changed.
        e.target.value = '';
    }, [onFiles, maxSizeMB]);

    const handleSelectSource = useCallback((source) => {
        setSheetOpen(false);
        sourceRefs.current[source]?.click();
    }, []);

    const accept = ACCEPT_BY_KIND[kind] || ACCEPT_BY_KIND.image;

    // Non-chooser platforms keep the original single-input markup verbatim.
    const singleInput = (
        <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleChange}
            className="hidden"
            aria-hidden="true"
            // data-testid lets tests dispatch a change event without needing
            // a separate ref; matches the existing PaintFormModal.test pattern
            // (which queries `input[type=file]` directly). Defaults to
            // 'file-picker-input'; override via the testId option when one
            // component mounts the hook twice.
            data-testid={testId}
        />
    );

    // The source whose input keeps the bare testid, so existing unit tests and
    // the E2E page objects still resolve to exactly one element. 'files' is the
    // closest analogue to the old single input; kinds without a Files tile fall
    // back to 'photos'. The others are suffixed with their source name.
    const primarySource = sources.includes('files') ? 'files' : 'photos';

    const chooserInputs = (
        <>
            {sources.map((source) => {
                const props = SOURCE_INPUT_PROPS[source](kind);
                return (
                    <input
                        key={source}
                        ref={(el) => { sourceRefs.current[source] = el; }}
                        type="file"
                        multiple={multiple}
                        onChange={handleChange}
                        className="hidden"
                        aria-hidden="true"
                        data-testid={
                            source === primarySource ? testId : `${testId}-${source}`
                        }
                        data-source={source}
                        {...props}
                    />
                );
            })}
            <FileSourceSheet
                isOpen={sheetOpen}
                onClose={() => setSheetOpen(false)}
                onSelect={handleSelectSource}
                sources={sources}
                title={sheetTitle}
            />
        </>
    );

    const fileInputElement = useChooser ? chooserInputs : singleInput;

    return { triggerPicker, fileInputElement };
};

export default useFilePicker;
